import { toExcelAmount, toExcelDate, toExcelQuantity } from 'components/xlsx/excelCells';
import { isEmployeePriced } from 'pages/pricelists/modal/employeePricing';
import { formatRateRange, trimNumber } from 'pages/pricelists/rateRange';
import { ClassifierData } from 'types/classifiers';
import { MatterEmployeeRoleType, MatterListExportItem } from 'types/matters';
import { EmployeeRate, Pricelist } from 'types/pricelists';
import { ReferralListItem, ReferralTypeCode } from 'types/referrals';
import { getClassifierText, t } from 'utils/getTranslations';
import { secondsToHours } from 'utils/time';

export const mapMattersToCSVFormat = (
    matters: MatterListExportItem[],
    classifiers: Record<string, ClassifierData>,
    options?: { isAdministratorFieldEnabled?: boolean }
) => {
    return matters.map((matter) => ({
        [t('matters.export.contact-name')]: matter.clientParty.name,
        [t('matters.export.case-number')]: matter.caseNumber,
        [t('matters.export.case-name')]: matter.name,
        [t('matters.export.description-of-case')]: matter.description,
        [t('matters.export.case-status')]: getClassifierText(
            classifiers['MATTER_STATE_TYPE'],
            matter.matterStateTypeCode
        ),
        [t('matters.export.case-opening-date')]: toExcelDate(matter.openedDate),
        [t('matters.export.case-closing-date')]: toExcelDate(matter.closedDate),
        [t('matters.export.supervising-colleague')]: matter.supervisedByEmployee?.name,
        [t('matters.export.responsible-colleague')]: matter.responsibleEmployee?.name,
        [t('matters.export.handled-by-employees')]: matter.employees
            ?.filter(
                (item) =>
                    item.matterEmployeeRoleTypeCode === MatterEmployeeRoleType.STAFF &&
                    // Only split the assistants out when the feature is on, so the column agrees
                    // with what the case view shows.
                    !(options?.isAdministratorFieldEnabled && item.administrator)
            )
            .map((item) => item.employee.name)
            .join(', '),
        [t('matters.export.handled-by-office')]: matter.handledByOffice?.name,
        [t('matters.export.service-agreement')]: matter.serviceAgreementContract?.contractName,
        [t('matters.export.case-price-list-currency')]: matter.pricelistCurrencyTypeCode,
        [t('matters.export.case-price-list')]: formatCasePricelist(matter.pricelist),
        [t('matters.export.total-hours')]: toExcelQuantity(
            matter.timecardSummarizedStats
                ? secondsToHours(matter.timecardSummarizedStats.totalBillableTimeInSeconds ?? 0, 2)
                : null
        ),
        [t('matters.export.total-cash')]: toExcelAmount(
            matter.timecardSummarizedStats?.totalBillableAmountInOriginalCurrency
        ),
        [t('matters.export.uninvoiced-hours')]: toExcelQuantity(
            matter.timecardSummarizedStats
                ? secondsToHours(matter.timecardSummarizedStats.uninvoicedTimeInSeconds ?? 0, 2)
                : null
        ),
        [t('matters.export.uninvoiced-cash')]: toExcelAmount(
            matter.timecardSummarizedStats?.uninvoicedAmountInOriginalCurrency
        ),
        [t('matters.export.invoiced-hours')]: toExcelQuantity(
            matter.timecardSummarizedStats
                ? secondsToHours(matter.timecardSummarizedStats.invoicedTimeInSeconds ?? 0, 2)
                : null
        ),
        [t('matters.export.final-invoiced-timecard-amount')]: toExcelAmount(
            matter.timecardSummarizedStats?.invoicedAmountInOriginalCurrency
        ),
        [t('matters.export.is-confidential')]: matter.isConfidential,
        [t('matters.export.last-timecard-date')]: toExcelDate(matter.lastTimecardDate),
        [t('matters.export.last-invoice-date')]: toExcelDate(matter.lastInvoiceInfo?.invoiceDate),
        [t('matters.export.last-invoice-status')]: getClassifierText(
            classifiers['INVOICE_STATUS_TYPE'],
            matter.lastInvoiceInfo?.invoiceStatusTypeCode
        ),
        [t('matters.export.payer')]: matter.payerParty?.name,
        [t('matters.export.business-area')]: (matter.partyBusinessAreas ?? [])
            .map(({ businessAreaCode }) => getClassifierText(classifiers['BUSINESS_AREA_TYPE'], businessAreaCode))
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .join(', '),
        [t('matters.export.practice-area')]: (matter.practiceAreas ?? [])
            .map((practiceArea) => practiceArea.name)
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .join(', '),
        [t('matters.export.practice-area-for-dms')]: matter.dmsPracticeArea?.name,
        [t('matters.export.second-level-practice-areas')]: (matter.secondLevelPracticeAreas ?? [])
            .map((practiceArea) => practiceArea.name)
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .join(', '),
        [t('matters.export.case-referred-by')]: formatReferrals(matter.referrals, classifiers),
        [t('matters.export.case-client-referred-by')]: formatReferrals(matter.clientReferrals, classifiers),
    }));
};

const formatPersonPrice = ({ employee, jobRoleName, hourlyPrice }: EmployeeRate): string =>
    `${employee.name}${jobRoleName ? ` (${jobRoleName})` : ''}: ${trimNumber(hourlyPrice ?? 0)}`;

const formatCasePricelist = (pricelist: Pricelist | undefined): string | undefined => {
    if (!pricelist) {
        return undefined;
    }
    if (isEmployeePriced(pricelist)) {
        const people = (pricelist.periods?.find((period) => period.inForce)?.rates ?? [])
            .filter((rate) => rate.hourlyPrice != null)
            .map(formatPersonPrice);
        const lines = pricelist.followsPricelist ? [pricelist.followsPricelist.name, ...people] : people;
        return lines.length > 0 ? lines.join('\n') : undefined;
    }
    // The file keeps a plain hyphenated range: it is data, where the screens set an en dash.
    return formatRateRange(pricelist.minHourlyPrice, pricelist.maxHourlyPrice, pricelist.avgHourlyPrice).replace(
        '–',
        ' - '
    );
};

const formatReferrals = (referrals: ReferralListItem[] | null, classifiers: Record<string, ClassifierData>) =>
    (referrals ?? [])
        .map(
            (referral) =>
                `${getReferralName(referral, classifiers)} (${getClassifierText(
                    classifiers['REFERRAL_TYPE'],
                    referral.referralTypeCode
                )})`
        )
        .join(', ');

const getReferralName = (
    referral: ReferralListItem,
    classifiers: Record<string, ClassifierData>
): string | undefined => {
    switch (referral.referralTypeCode) {
        case ReferralTypeCode.CONTACT: {
            return [referral.companyParty?.name, referral.personParty?.name].filter(Boolean).join(' & ');
        }
        case ReferralTypeCode.EMPLOYEE: {
            return referral.employee?.name;
        }
        case ReferralTypeCode.OTHER: {
            return getClassifierText(classifiers['REFERRAL_OTHER_SOURCE_TYPE'], referral.otherSourceTypeCode);
        }
        default: {
            return undefined;
        }
    }
};
